import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { apiClient } from '../config/api';
import { debug } from '../utils/debugUtils';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setLoading] = useState(true);
  const [token, setToken] = useState(null);
  const location = useLocation();

  // Public routes that don't require authentication
  const publicRoutes = ['/quote-response', '/about', '/services'];

  // Simple function to check auth status
  const checkAuth = async () => {
    try {
      const response = await apiClient.get('/auth/me');
      setIsAuthenticated(true);
      return true;
    } catch (error) {
      debug.error('Auth check failed:', error);
      setIsAuthenticated(false);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Logout function that clears auth state and calls backend logout
  const logout = async () => {
    try {
      // Call backend logout endpoint
      await apiClient.get('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Always clear local state regardless of backend response
      setIsAuthenticated(false);
      setToken(null);
      setLoading(false);
      
      // Force navigation to login page
      window.location.href = '/';
    }
  };

  useEffect(() => {
    // Skip authentication check for public routes
    const isPublicRoute = publicRoutes.some(route => location.pathname.startsWith(route));
    
    if (isPublicRoute) {
      // For public routes, set loading to false but don't check auth
      setLoading(false);
      setIsAuthenticated(false);
    } else {
      // For protected routes, check authentication
      checkAuth();
    }
  }, [location.pathname]);

  return (
    <AuthContext.Provider value={{ 
      isAuthenticated, 
      checkAuth, 
      logout,
      isLoading,
      token,
      setToken
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};